<template>
  <key-value-list v-bind="$attrs" :variant="variant" :size="size">
    <div v-for="formField in consolidatedFields" :key="formField.id" class="custom-form-field">
      <key-value>
        <template #label>
          {{ formField.label }}
          <span v-if="formField.required" class="text-red"><strong>*</strong></span>
        </template>

        <template #value>
          <custom-forms-multiple-trigger-form-field
            :data-testid="`custom-forms-field-${formField.testId}`"
            :components-mapping="componentsMapping"
            :entity-name="formField.entityName"
            :form-field="formField"
            :timezone="timezone"
            :military-time-enabled="militaryTimeEnabled"
            @update:formField="handleFormFieldUpdate"
            :is-editable="isEditable"
            :is-loading="isLoading" />
        </template>
      </key-value>
    </div>
  </key-value-list>
</template>
<script>
import { ref, defineComponent, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { isEmpty, isDefined } from 'class-validator';
import { getCustomFieldFormattedValue, CustomFieldType } from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import CustomFormsMultipleTriggerFormField from './CustomFormsMultipleTriggerFormField.vue';
import KeyValueList from '@satellite/components/elements/key_value/KeyValueList.vue';
import KeyValue from '@satellite/components/elements/key_value/KeyValue.vue';
import { $t } from '@satellite/plugins/translator';

export default defineComponent({
  name: 'CustomFormsMultipleTriggerForm',
  components: {
    CustomFormsMultipleTriggerFormField,
    KeyValueList,
    KeyValue
  },
  emits: ['update:formField'],
  props: {
    objectId: {
      type: String,
      required: true
    },
    warehouseId: {
      type: String,
      required: true
    },
    triggers: {
      type: Array,
      required: true
    },
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: true
    },
    variant: {
      type: String,
      required: false,
      default: 'list',
      validator(value) {
        return ['list', 'list-stacked', 'list-inline', 'grid'].includes(value);
      }
    },
    size: {
      type: String,
      required: false,
      default: 'regular',
      validator(value) {
        return ['regular', 'small'].includes(value);
      }
    },
    isEditable: {
      type: Boolean,
      required: false,
      default: false
    },
    componentsMapping: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  setup(props, { emit }) {
    const consolidatedFields = ref([]);
    const isLoading = ref(false);

    /**
     * This is a "hack" so we can access the eventHub from a Component API-based
     * component.
     */
    const getEventHub = () => {
      let eventHub = null;

      try {
        const app = getCurrentInstance()?.appContext.app;
        eventHub = app.config.globalProperties.$eventHub;
      } catch (error) {
        console.error('[CustomFormsMultipleTriggerForm] Error retrieving eventHub instance', error);
      }

      return eventHub;
    };

    const getCustomFormData = async () => {
      isLoading.value = true;

      if (props.objectId && props.triggers?.length > 0) {
        try {
          const formData = await customFormsService.getCustomFormData({
            s: {
              objectId: props.objectId,
              triggerId: { $in: props.triggers.map(t => t.id) },
              formFieldId: { $notnull: true }
            },
            limit: 1000
          });

          consolidatedFields.value = consolidatedFields.value
            .map(formField => {
              const existingFormDataField = formData.find(
                f => f.formField?.id === formField.id && f.triggerId === formField.triggerId
              );
              const trigger = props.triggers.find(t => t.id === formField.triggerId);

              formField.label = formField.overrideLabel || formField.field.label;
              formField.objectId = props.objectId;
              formField.warehouseId = props.warehouseId;
              formField.testId = formField.id;
              formField.entityName = trigger?.dataEntityName;

              if (existingFormDataField) {
                formField.value = existingFormDataField.value;
                formField.label = formField.overrideLabel || formField.field.label;
                formField.type = formField.field.type;
                // This will be used to update the value on customformdata
                formField.dataId = existingFormDataField.id;

                formField.formattedValue = formattedValue(formField);
              } else {
                formField.formattedValue = '--';
              }

              return formField;
            })
            .sort((a, b) => a.order - b.order);
        } catch (error) {
          console.error($t('customForms.common.messages.errorFetchingCustomFormData'), error);
        } finally {
          isLoading.value = false;
        }
      }
    };

    const formattedValue = field => {
      if (
        field.value === '' ||
        !isDefined(field.value) ||
        isEmpty(field.value) ||
        (Array.isArray(field.value) && field.value.length === 0)
      ) {
        return '--';
      }
      return getCustomFieldFormattedValue(field, {
        [CustomFieldType.Document]: { generateLink: true },
        [CustomFieldType.MultiDocument]: { generateLink: true },
        [CustomFieldType.Timestamp]: {
          timezone: props.timezone,
          formatAsMilitary: props.militaryTimeEnabled
        }
      });
    };

    const setFields = async () => {
      // All the fields from the triggers (trigger.flow.formFrom.formFields)
      consolidatedFields.value = props.triggers.flatMap(trigger =>
        (trigger.flow?.formFrom?.formFields || []).map(formField => ({
          ...formField,
          triggerId: trigger.id
        }))
      );

      // Fetch the submitted values, up to this point, we have the triggers
      // Which contains the fields definition
      await getCustomFormData();
    };

    const handleFormFieldUpdate = async $event => {
      await getCustomFormData();
      emit('update:formField', $event);
    };

    const handleCustomFormDataUpdated = event => {
      const triggers = Array.isArray(props.triggers) ? props.triggers : [];
      const needsToFetchCustomFormData = triggers.some(trigger => {
        return trigger.id === event.triggerId;
      });

      if (needsToFetchCustomFormData) {
        getCustomFormData();
      }
    };

    onBeforeMount(() => {
      setFields();

      const eventHub = getEventHub();
      if (eventHub) {
        eventHub.$on('update-CustomFormData', handleCustomFormDataUpdated);
      }
    });

    onBeforeUnmount(() => {
      const eventHub = getEventHub();
      if (eventHub) {
        eventHub.$off('update-CustomFormData', handleCustomFormDataUpdated);
      }
    });

    return {
      isLoading,
      consolidatedFields,
      formattedValue,
      handleFormFieldUpdate
    };
  }
});
</script>
